import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  HStack,
  Heading,
  Text,
  Box,
  Icon,
  Tooltip,
  Badge,
  Divider,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  Link,
} from '@chakra-ui/react';
import { PhoneIcon, EmailIcon, InfoOutlineIcon, CalendarIcon, LinkIcon } from '@chakra-ui/icons';
import { Client } from '../../api/clientApi';

interface ClientViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
}

const InfoRow = ({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ElementType }) => (
  <HStack w="100%" align="start" spacing={3}>
    {icon && <Box pt={0.5}><Icon as={icon} color="blue.400" boxSize={5} /></Box>}
    <Tooltip label={label} hasArrow placement="top-start">
      <Text fontWeight="semibold" color="gray.600" minW="140px">{label}:</Text>
    </Tooltip>
    <Text color="gray.800" wordBreak="break-all">{value || <span style={{color:'#bbb'}}>-</span>}</Text>
  </HStack>
);

const ClientViewModal: React.FC<ClientViewModalProps> = ({ isOpen, onClose, client }) => {
  if (!client) return null;

  // Calculate analytics for this client
  const projectCount = Array.isArray(client.project) ? client.project.length : 0;
  const completedFields = [
    client.clientName,
    client.additionalInformation?.leadPersonName,
    client.additionalInformation?.email,
    client.additionalInformation?.phone,
    client.additionalInformation?.secondaryContact,
    client.additionalInformation?.websiteUrl,
  ].filter(field => field !== undefined && field !== null && field !== '').length;
  
  const totalFields = 6;
  const completionPercentage = Math.round((completedFields / totalFields) * 100);

  // Get relative time
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} week${Math.ceil(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} month${Math.ceil(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `${Math.ceil(diffDays / 365)} year${Math.ceil(diffDays / 365) > 1 ? 's' : ''} ago`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="2xl" boxShadow="2xl" bgGradient="linear(to-br, white, blue.25)" maxH="90vh" overflowY="auto">
        <ModalHeader fontWeight="bold" color="blue.700" fontSize="2xl" borderBottomWidth={1} borderColor="blue.100" pb={4}>
          <VStack align="start" spacing={2}>
            <Text>{client.clientName}</Text>
            <HStack spacing={3}>
              <Badge colorScheme={projectCount > 0 ? 'blue' : 'gray'} fontSize="sm" px={3} py={1}>
                {projectCount} Project{projectCount !== 1 ? 's' : ''}
              </Badge>
              <Badge 
                colorScheme={completionPercentage >= 80 ? 'green' : completionPercentage >= 60 ? 'yellow' : 'red'}
                fontSize="sm" 
                px={3} 
                py={1}
              >
                {completionPercentage}% Complete
              </Badge>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6} px={6}>
          <VStack align="start" spacing={6} w="100%">
            
            {/* Client Overview Stats */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} w="100%">
              <GridItem>
                <Stat bg="blue.50" p={4} borderRadius="lg" border="1px" borderColor="blue.200">
                  <StatLabel color="blue.600">Active Projects</StatLabel>
                  <StatNumber color="blue.700">{projectCount}</StatNumber>
                  <StatHelpText color="blue.500">Attached projects</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat bg="green.50" p={4} borderRadius="lg" border="1px" borderColor="green.200">
                  <StatLabel color="green.600">Profile Complete</StatLabel>
                  <StatNumber color="green.700">{completionPercentage}%</StatNumber>
                  <StatHelpText color="green.500">{completedFields}/{totalFields} fields</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat bg="purple.50" p={4} borderRadius="lg" border="1px" borderColor="purple.200">
                  <StatLabel color="purple.600">Client Since</StatLabel>
                  <StatNumber color="purple.700" fontSize="lg">
                    {new Date(client.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </StatNumber>
                  <StatHelpText color="purple.500">{getRelativeTime(client.createdAt)}</StatHelpText>
                </Stat>
              </GridItem>
            </Grid>

            {/* Projects Section */}
            {projectCount > 0 && (
              <Box w="100%">
                <Heading size="sm" mb={3} color="blue.600" display="flex" alignItems="center" gap={2}>
                  <Icon as={InfoOutlineIcon} />
                  Attached Projects
                </Heading>
                <Wrap spacing={2}>
                  {client.project.map((proj: any, idx: number) => (
                    <WrapItem key={proj._id || proj.id || proj || idx}>
                      <Tag size="lg" colorScheme="blue" variant="solid" borderRadius="full">
                        <TagLabel>
                          {typeof proj === 'string' 
                            ? `Project ${proj.slice(-6)}` 
                            : (proj.projectName || proj.name || `Project ${idx + 1}`)
                          }
                        </TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}

            <Divider />

            {/* Contact Information */}
            <Box w="100%">
              <Heading size="sm" mb={4} color="blue.600" display="flex" alignItems="center" gap={2}>
                <Icon as={PhoneIcon} />
                Contact Information
              </Heading>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} w="100%">
                <GridItem>
                  <VStack align="start" spacing={3}>
                    <InfoRow label="Lead Person" value={client.additionalInformation?.leadPersonName} icon={InfoOutlineIcon} />
                    <InfoRow label="Email" value={client.additionalInformation?.email} icon={EmailIcon} />
                    <InfoRow label="Phone" value={client.additionalInformation?.phone} icon={PhoneIcon} />
                    <InfoRow label="Secondary Contact" value={client.additionalInformation?.secondaryContact} icon={InfoOutlineIcon} />
                    {client.additionalInformation?.websiteUrl?.trim() ? (
                      <HStack w="100%" align="start" spacing={3}>
                        <Box pt={0.5}><Icon as={LinkIcon} color="blue.400" boxSize={5} /></Box>
                        <Text fontWeight="semibold" color="gray.600" minW="140px">Website URL:</Text>
                        <Link
                          href={
                            client.additionalInformation.websiteUrl.startsWith('http')
                              ? client.additionalInformation.websiteUrl
                              : `https://${client.additionalInformation.websiteUrl}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          color="blue.600"
                          wordBreak="break-all"
                        >
                          {client.additionalInformation.websiteUrl}
                        </Link>
                      </HStack>
                    ) : (
                      <InfoRow label="Website URL" value={null} icon={LinkIcon} />
                    )}
                  </VStack>
                </GridItem>
                <GridItem>
                  <VStack align="start" spacing={3}>
                    <InfoRow label="Created Date" value={new Date(client.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })} icon={CalendarIcon} />
                  </VStack>
                </GridItem>
              </Grid>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ClientViewModal;
